import React, { useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/constants';
import { extractDescriptionSummary } from '@cotera/client/app/components/tools/utils';
import type { ToolRendererProps } from './index';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import { ToolErrorRow, toolErrorText } from './tool-error-row';

type SearchToolInput = {
  providers?: string[] | null;
  query?: string | null;
  limit?: number | null;
};

type ToolSearchRow = {
  key: string;
  name: string;
  description: string;
  provider: string;
};

type SkillSearchRow = {
  key: string;
  name: string;
  description: string;
};

type ResourceSearchRow = {
  id?: string;
  title?: string | null;
  name?: string | null;
  description?: string | null;
};

export type SearchCoteraItem =
  | { t: 'tool'; data: ToolSearchRow }
  | { t: 'skill'; data: SkillSearchRow }
  | { t: 'chat'; data: ResourceSearchRow }
  | { t: 'agent'; data: ResourceSearchRow }
  | { t: 'folder'; data: ResourceSearchRow }
  | { t: 'document'; data: ResourceSearchRow }
  | { t: 'workflow'; data: ResourceSearchRow }
  | { t: 'workflow_trigger'; data: ResourceSearchRow };

type SearchToolOutput = {
  items: SearchCoteraItem[];
  totalCount: number;
};

function firstNonEmptyString(
  ...values: Array<string | null | undefined>
): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function resourceTitle(item: SearchCoteraItem): string {
  if (item.t === 'tool' || item.t === 'skill') {
    return item.data.name;
  }

  const row = item.data;
  const titled = firstNonEmptyString(
    item.t === 'folder' ||
      item.t === 'workflow' ||
      item.t === 'workflow_trigger'
      ? row.name
      : null,
    row.title,
    row.description
  );
  if (titled !== null) {
    return titled.length > 80 ? titled.slice(0, 80) : titled;
  }
  if (typeof row.id === 'string' && row.id.length > 0) {
    return row.id.slice(0, 8);
  }
  return item.t;
}

export const SearchToolRenderer: React.FC<ToolRendererProps> = ({ part }) => {
  const input = part.input as SearchToolInput | undefined;
  const provider = input?.providers?.[0];
  const query = input?.query;
  const label = [query, provider].filter(Boolean).join(' ') || undefined;

  if (part.isIncomplete()) {
    return <SearchStatusRow status="pending" query={label} />;
  }

  if (part.isError()) {
    return (
      <ToolErrorRow
        title={
          label !== undefined
            ? `Search for “${label}” encountered an error`
            : 'Cotera search encountered an error'
        }
        errorText={toolErrorText(part)}
        input={part.input}
        icon={<SearchIcon className="h-4 w-4 shrink-0" />}
      />
    );
  }

  const output = part.getOutput<SearchToolOutput>();
  if (output === undefined) {
    return null;
  }

  return <SearchResultCard output={output} query={label} />;
};

const SearchStatusRow: React.FC<{
  status: 'pending' | 'complete';
  query?: string | null;
  hasResults?: boolean;
}> = ({ status, query, hasResults }) => {
  const label = useMemo(() => {
    const quoted =
      query !== undefined && query !== null && query.trim().length > 0
        ? `"${query.trim()}"`
        : undefined;

    if (status === 'pending') {
      return quoted !== undefined
        ? `Searching Cotera for ${quoted}`
        : 'Searching Cotera';
    }
    if (hasResults === false) {
      return quoted !== undefined ? `No matches for ${quoted}` : 'No matches';
    }
    return quoted !== undefined ? `Matches for ${quoted}` : 'Search results';
  }, [status, query, hasResults]);

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
      <SearchIcon className="h-4 w-4" />
      {status === 'complete' ? (
        <CheckIcon className="h-4 w-4 text-success" />
      ) : null}
      {status === 'pending' ? (
        <RunningToolTitle className="italic text-sm">{label}</RunningToolTitle>
      ) : (
        <span className="italic">{label}</span>
      )}
    </div>
  );
};

const SearchResultCard: React.FC<{
  output: SearchToolOutput;
  query?: string | null;
}> = ({ output, query }) => {
  const [expanded, setExpanded] = useState(false);
  const items = output.items ?? [];
  const hasResults = items.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <SearchStatusRow
          status="complete"
          query={query}
          hasResults={hasResults}
        />
        <button
          type="button"
          className="cursor-pointer text-muted-foreground hover:text-foreground focus:outline-none focus:ring-0"
          onClick={() => setExpanded((current) => !current)}
          aria-label={expanded ? 'Hide search results' : 'Show search results'}
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${
              expanded ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </button>
      </div>
      {expanded ? (
        <div className="border border-border rounded-lg bg-card/60 px-4 py-3">
          {hasResults ? (
            <div className="space-y-3">
              {items.map((item, index) => {
                if (item.t === 'tool') {
                  const tool = item.data;
                  const Icon = PROVIDER_ICONS[tool.provider];
                  return (
                    <div
                      key={`tool-${tool.key}-${index}`}
                      className="flex items-start gap-3 text-sm"
                    >
                      {Icon !== undefined ? (
                        <span className="h-5 w-5 flex items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
                          <Icon />
                        </span>
                      ) : (
                        <span className="h-5 w-5 rounded-full bg-muted" />
                      )}
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Tool
                        </div>
                        <div className="text-foreground font-medium">
                          {tool.name}
                        </div>
                        <div className="text-muted-foreground line-clamp-2">
                          {tool.description.length > 0
                            ? extractDescriptionSummary(tool.description)
                            : null}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {tool.key}
                        </div>
                      </div>
                    </div>
                  );
                }

                if (item.t === 'skill') {
                  const skill = item.data;
                  return (
                    <div
                      key={`skill-${skill.key}-${index}`}
                      className="flex items-start gap-3 text-sm"
                    >
                      <span className="h-5 w-5 rounded-md bg-muted flex items-center justify-center text-[10px] font-semibold uppercase text-muted-foreground shrink-0">
                        S
                      </span>
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Skill
                        </div>
                        <div className="text-foreground font-medium">
                          {skill.name}
                        </div>
                        <div className="text-muted-foreground line-clamp-2">
                          {skill.description.length > 0
                            ? extractDescriptionSummary(skill.description)
                            : null}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {skill.key}
                        </div>
                      </div>
                    </div>
                  );
                }

                const title = resourceTitle(item);
                return (
                  <div
                    key={`${item.t}-${index}`}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span className="h-5 w-5 rounded-md bg-muted flex items-center justify-center text-[10px] font-semibold uppercase text-muted-foreground shrink-0">
                      {item.t.slice(0, 1)}
                    </span>
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {item.t}
                      </div>
                      <div className="text-foreground font-medium">{title}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No results.</div>
          )}
        </div>
      ) : null}
    </div>
  );
};
